import React, { useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Divider,
  ScheduleIcon,
  Switch,
  Text,
} from '@billda/design-system';
import { usePermission, Permissions } from '@billda/permissions-ui';
import {
  useOrgAutoSubmitOverview,
  useSetOrgAutoSubmit,
} from '../hooks/useOrgAutoSubmit';
import { shapeOverview } from '../utils/autoSubmitOverview';

const CONSEQUENCES = [
  'It applies from each gang’s and contractor’s next pot. Pots already open keep their current schedule.',
  'Gang leaders and contractors can still turn auto-submit off for themselves, and their own choice always wins.',
  'Pots with nothing approved are skipped, and pots with a negative total are sent back instead of submitted.',
];

/**
 * Auto-submit settings area: org-wide default toggle plus a read-only overview
 * of gang/contractor settings. Turning the default on requires confirmation;
 * turning it off is immediate.
 */
export const AutoSubmitArea: React.FC = () => {
  const canManage = usePermission(Permissions.Organisation.Manage);
  const { data, isLoading } = useOrgAutoSubmitOverview();
  const setOrgAutoSubmit = useSetOrgAutoSubmit();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isEnabled = data?.isEnabled ?? false;
  const overview = shapeOverview(isEnabled, data?.entries ?? []);
  const toggleDisabled = isLoading || !canManage || setOrgAutoSubmit.isPending;

  const handleToggle = (checked: boolean) => {
    if (checked) {
      setConfirmOpen(true);
      return;
    }
    setOrgAutoSubmit.mutate(false);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    setOrgAutoSubmit.mutate(true);
  };

  return (
    <>
      <div className="mb-6 rounded-xl border border-border-default bg-surface-default p-6">
        <div className="mb-4 flex items-center">
          <ScheduleIcon
            aria-hidden="true"
            className="mr-2 size-5 text-content-accent"
          />
          <Text variant="h6">Auto-submit</Text>
        </div>

        <Text as="div" variant="body2" color="secondary" className="mb-6">
          Set the organisation default for auto-submitting pots at the submit
          deadline. It applies to each new pot as it opens; gangs and
          contractors who set their own choice keep it.
        </Text>

        <Divider className="mb-6" />

        <div className="flex items-start gap-4">
          <Switch
            id="org-auto-submit-toggle"
            checked={isEnabled}
            disabled={toggleDisabled}
            onCheckedChange={handleToggle}
            data-testid="org-auto-submit-toggle"
          />
          <label
            htmlFor="org-auto-submit-toggle"
            className="flex cursor-pointer flex-col gap-1"
          >
            <Text as="span" variant="body1" className="font-medium">
              Auto-submit all pots
            </Text>
            <Text as="span" variant="body2" color="secondary">
              From the next pot onwards, every pot is scheduled to submit
              automatically when the submit deadline passes. Gang leaders and
              contractors can still turn it off for themselves or submit
              earlier.
            </Text>
          </label>
        </div>
      </div>

      <div
        className="mb-6 rounded-xl border border-border-default bg-surface-default p-6"
        data-testid="auto-submit-overview"
      >
        <Text as="div" variant="h6" className="mb-4">
          {overview.header}
        </Text>

        <Divider className="mb-4" />

        {overview.emptyMessage ? (
          <Text as="div" variant="body2" color="secondary">
            {overview.emptyMessage}
          </Text>
        ) : (
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-border-default">
                <th className="type-label px-2 py-2 text-content-secondary">
                  Name
                </th>
                <th className="type-label px-2 py-2 text-content-secondary">
                  Type
                </th>
                <th className="type-label px-2 py-2 text-content-secondary">
                  Auto-submit
                </th>
                <th className="type-label px-2 py-2 text-content-secondary">
                  Source
                </th>
              </tr>
            </thead>
            <tbody>
              {overview.rows.map(row => (
                <tr
                  key={`${row.typeLabel}-${row.id}`}
                  className="border-b border-border-default transition-colors last:border-b-0 hover:bg-surface-raised"
                >
                  <td className="type-body-md px-2 py-2 text-content-primary">
                    {row.name}
                  </td>
                  <td className="px-2 py-2">
                    <Chip label={row.typeLabel} size="sm" />
                  </td>
                  <td className="px-2 py-2">
                    <Chip
                      label={row.stateLabel}
                      size="sm"
                      color={row.stateLabel === 'On' ? 'success' : 'default'}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <Text variant="body2" color="secondary">
                      {row.sourceLabel}
                    </Text>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {overview.footerCaption && (
          <Text
            as="div"
            variant="caption"
            color="secondary"
            className="mt-4 block"
          >
            {overview.footerCaption}
          </Text>
        )}
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            setConfirmOpen(false);
          }
        }}
      >
        <DialogContent data-testid="org-auto-submit-confirm-dialog">
          <DialogHeader>Turn on auto-submit for all pots?</DialogHeader>
          <ul className="list-disc pl-5">
            {CONSEQUENCES.map(text => (
              <li key={text} className="type-body-md text-content-primary">
                {text}
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="neutral"
              onClick={handleConfirm}
              data-testid="org-auto-submit-confirm-button"
            >
              Turn on auto-submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
